/**
 * Marriage Application Approval Detail Page
 * Displays detailed information for a marriage certificate application with approval actions
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import { applicantService } from "../services/api/applicant.service";
import { workflowService } from "../services/api/workflow.service";
import { officiatorService } from "../services/api/officiator.service";
import { notificationService } from "../services/api/notification.service";
import type {
  CertificateApplication,
  County,
  Nationality,
} from "../types/certificate.types";
import type { Applicant } from "../types/applicant.types";
import type { Officiator } from "../types/officiator.types";
import type { QueryWorkflowActionDto } from "../types/workflow.types";
import { ApplicantType } from "../types/applicant.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import WorkflowStages from "../components/common/WorkflowStages";
import History from "../components/common/History";
import QueryRemarks from "../components/common/QueryRemarks";
import QueryApplicationForm from "../components/common/QueryApplicationForm";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showSuccessToast, showErrorToast, showConfirm } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import { getFullPath } from "../utils/getFullPath";
import "../styles/pages/UserDetail.css";
import "../styles/pages/Users.css";

export const ApprovalDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [application, setApplication] = useState<CertificateApplication | null>(
    null,
  );
  const [, setCounties] = useState<County[]>([]);
  const [, setNationalities] = useState<Nationality[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentStageOrder, setCurrentStageOrder] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showQueryRemarks, setShowQueryRemarks] = useState(false);
  const [hasQueryRemarks, setHasQueryRemarks] = useState(false);
  const [showQueryForm, setShowQueryForm] = useState(false);

  // Applicants state
  const [groomApplicant, setGroomApplicant] = useState<Applicant | null>(null);
  const [brideApplicant, setBrideApplicant] = useState<Applicant | null>(null);
  const [officiator, setOfficiator] = useState<Officiator | null>(null);

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Application Approval", "Review and approve application");
    fetchData();
  }, [id, setPageTitle]);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError("");
      const [appData, countiesData, nationalitiesData] = await Promise.all([
        certificateService.getCertificateApplicationById(parseInt(id, 10)),
        certificateService.getCounties(),
        certificateService.getNationalities(),
      ]);
      setApplication(appData);
      setCounties(Array.isArray(countiesData) ? countiesData : []);
      setNationalities(
        Array.isArray(nationalitiesData) ? nationalitiesData : [],
      );

      // Fetch applicants and officiator
      try {
        const applicants = await applicantService.getApplicantsByCertificateId(
          parseInt(id, 10),
        );
        const groom = applicants.find(
          (a: Applicant) => a.applicantType === ApplicantType.Groom,
        );
        const bride = applicants.find(
          (a: Applicant) => a.applicantType === ApplicantType.Bride,
        );
        setGroomApplicant(groom || null);
        setBrideApplicant(bride || null);
      } catch (err) {
        console.error("Error fetching applicants:", err);
      }

      // Fetch officiator
      try {
        const officiatorData =
          await officiatorService.getOfficiatorsByCertificateId(
            parseInt(id, 10),
          );
        setOfficiator(
          Array.isArray(officiatorData) && officiatorData.length > 0
            ? officiatorData[0]
            : null,
        );
      } catch (err) {
        console.error("Error fetching officiator:", err);
      }

      // Check if workflow is at user's approval level and get current stage
      if (appData.workflowId) {
        try {
          const currentStage = await workflowService.getCurrentStageRole(
            appData.workflowId,
            appData.id!,
          );
          setCurrentStageOrder(currentStage.stageOrder);
        } catch (err) {
          console.error("Error checking approval level:", err);
          setCurrentStageOrder(0);
        }
      } else {
        setCurrentStageOrder(0);
      }

      try {
        const remarks = await workflowService.getRequeryRemarks(
          "CertificateApplications",
          appData.id!,
        );
        setHasQueryRemarks(remarks.length > 0);
      } catch {
        setHasQueryRemarks(false);
      }
    } catch (err: any) {
      console.error("Error fetching application:", err);
      setError(err?.message || "Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!application || !application.workflowId) return;
    const confirmed = await showConfirm(
      "Approve Application",
      `Are you sure you want to approve application "${application.formNo}"?`,
      "Yes, approve",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setActionLoading(true);

      // Get workflow details to check if this is the final stage
      const workflow = await workflowService.getWorkflowById(
        application.workflowId,
      );
      const totalStages = workflow.stages?.length || 0;
      const isFinalStage = currentStageOrder >= totalStages;

      // Approve the workflow action
      await workflowService.approve({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: "Application approved",
      });

      // If this is the final approval stage, update status to Approved
      if (isFinalStage) {
        await certificateService.changeStatus(
          application.id!,
          UserStatusId.Approved,
        );
      }

      await fetchData();
      showSuccessToast("Application has been approved successfully.");
      // Send email notification if settings allow
      notificationService
        .sendEmailNotification(application.workflowId, application.id!)
        .catch(() => {});
      navigate("/marriages/approval");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuery = () => {
    if (!application || !application.workflowId) return;
    setShowQueryForm(true);
  };

  const handleSubmitQuery = async (payload: QueryWorkflowActionDto) => {
    if (!application || !application.workflowId) return;
    try {
      setActionLoading(true);

      await workflowService.query(payload);

      // Change status to Queried to move the workflow back one step
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Queried,
      );

      await fetchData();
      showSuccessToast("Application has been queried successfully.");
      navigate("/marriages/approval");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!application || !application.workflowId) return;

    // Import Swal for custom input dialog
    const Swal = (await import("sweetalert2")).default;

    const result = await Swal.fire({
      title: "Reject Application",
      html: `
        <p>Please provide a reason for rejecting application "${application.formNo}":</p>
      `,
      input: "textarea",
      inputPlaceholder: "Enter your rejection reason here...",
      inputAttributes: {
        "aria-label": "Rejection reason",
        style: "min-height: 100px;",
      },
      showCancelButton: true,
      confirmButtonText: "Reject Application",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return "You must provide a reason for rejection!";
        }
      },
    });

    if (!result.isConfirmed || !result.value || !result.value.trim()) return;

    const rejectComment = result.value.trim();

    try {
      setActionLoading(true);

      // Reject the workflow action with the provided comment
      await workflowService.reject({
        workflowId: application.workflowId,
        recordId: application.id!,
        entityName: "CertificateApplications",
        comments: rejectComment,
      });

      // Change status to Rejected
      await certificateService.changeStatus(
        application.id!,
        UserStatusId.Rejected,
      );

      await fetchData();
      showSuccessToast("Application has been rejected successfully.");
      navigate("/marriages/approval");
    } catch (err: any) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  // Read-only passport photo (same source as the apply page) with no upload/remove option.
  const renderApplicantPhoto = (
    applicant: Applicant | null,
    altLabel: string,
  ) => {
    const photoUrl = getFullPath(
      applicant?.photoUrl || applicant?.profileImagePath,
    );
    return (
      <div className="applicant-photo-col">
        <div className="user-detail-field-label">Passport Size Photo</div>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={altLabel}
            style={{
              width: "120px",
              height: "150px",
              objectFit: "cover",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              marginTop: "0.25rem",
            }}
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.style.border = "2px solid red";
              img.alt = "Failed to load image";
            }}
          />
        ) : (
          <div className="user-detail-field-value empty">—</div>
        )}
      </div>
    );
  };

  if (!HasPermission(ModuleId.Weddings, ApplicationPermissionId.Approve)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to approve marriage certificates
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="user-detail-page">
          <BusyOverlay
            isVisible={loading || actionLoading}
            message={
              actionLoading ? "Processing..." : "Loading application details..."
            }
          />

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button onClick={() => setError("")} className="alert-close">
                ✕
              </button>
            </div>
          )}

          {application && (
            <div className="user-detail-layout">
              <button
                className="user-detail-back user-detail-back--mobile"
                onClick={() => navigate("/marriages/approval")}
              >
                ← Back to Approvals
              </button>

              {/* Column 1: Application Details */}
              <div className="user-detail-info">
                {/* Profile header */}
                <div className="user-detail-profile-header">
                  <div className="user-detail-avatar">💍</div>
                  <div className="user-detail-profile-info">
                    <h2>{application.formNo}</h2>
                    <div className="user-detail-profile-meta">
                      <span>{application.category}</span>
                      <span className="divider">•</span>
                      <span className={`status-badge status-badge--active`}>
                        {application.status?.name ||
                          application.statusName ||
                          "Submitted"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Marriage Application Information */}
                <h3 className="user-detail-section-title">
                  Marriage Application Information
                </h3>
                <div
                  className="user-detail-grid"
                  style={{
                    gridTemplateColumns: "repeat(3, 1fr)",
                  }}
                >
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Category</div>
                    <div className="user-detail-field-value">
                      {application.category}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">County</div>
                    <div
                      className={`user-detail-field-value ${!application.countyName ? "empty" : ""}`}
                    >
                      {application.countyName || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Place of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.placeOfWedding ? "empty" : ""}`}
                    >
                      {application.placeOfWedding || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Marriage
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfWedding ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfWedding) || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">
                      Date of Certification
                    </div>
                    <div
                      className={`user-detail-field-value ${!application.dateOfCertification ? "empty" : ""}`}
                    >
                      {formatDate(application.dateOfCertification) || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Email</div>
                    <div
                      className={`user-detail-field-value ${!application.email ? "empty" : ""}`}
                    >
                      {application.email || "—"}
                    </div>
                  </div>
                  <div className="user-detail-field">
                    <div className="user-detail-field-label">Created Date</div>
                    <div
                      className={`user-detail-field-value ${!application.createdAt ? "empty" : ""}`}
                    >
                      {formatDate(application.createdAt) || "—"}
                    </div>
                  </div>
                </div>

                {/* Groom's Details */}
                {groomApplicant && (
                  <>
                    <h3 className="user-detail-section-title">
                      Groom's Details
                    </h3>
                    <div
                      className="applicant-detail-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: "1.5rem",
                        alignItems: "start",
                      }}
                    >
                      {renderApplicantPhoto(groomApplicant, "Groom Photo")}
                      <div className="user-detail-grid">
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">Full Name</div>
                        <div className="user-detail-field-value">
                          {groomApplicant.firstName}{" "}
                          {groomApplicant.middleName || ""}{" "}
                          {groomApplicant.lastName}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Date of Birth
                        </div>
                        <div className="user-detail-field-value">
                          {formatDate(groomApplicant.dateOfBirth) || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Primary Phone
                        </div>
                        <div className="user-detail-field-value">
                          {groomApplicant.primaryPhone || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">Email</div>
                        <div
                          className={`user-detail-field-value ${!groomApplicant.email ? "empty" : ""}`}
                        >
                          {groomApplicant.email || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          National ID
                        </div>
                        <div
                          className={`user-detail-field-value ${!groomApplicant.nationalIdNumber ? "empty" : ""}`}
                        >
                          {groomApplicant.nationalIdNumber || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Current Address
                        </div>
                        <div className="user-detail-field-value">
                          {groomApplicant.currentAddress || "—"}
                        </div>
                      </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Bride's Details */}
                {brideApplicant && (
                  <>
                    <h3 className="user-detail-section-title">
                      Bride's Details
                    </h3>
                    <div
                      className="applicant-detail-row"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        gap: "1.5rem",
                        alignItems: "start",
                      }}
                    >
                      {renderApplicantPhoto(brideApplicant, "Bride Photo")}
                      <div className="user-detail-grid">
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">Full Name</div>
                        <div className="user-detail-field-value">
                          {brideApplicant.firstName}{" "}
                          {brideApplicant.middleName || ""}{" "}
                          {brideApplicant.lastName}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Date of Birth
                        </div>
                        <div className="user-detail-field-value">
                          {formatDate(brideApplicant.dateOfBirth) || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Primary Phone
                        </div>
                        <div className="user-detail-field-value">
                          {brideApplicant.primaryPhone || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">Email</div>
                        <div
                          className={`user-detail-field-value ${!brideApplicant.email ? "empty" : ""}`}
                        >
                          {brideApplicant.email || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          National ID
                        </div>
                        <div
                          className={`user-detail-field-value ${!brideApplicant.nationalIdNumber ? "empty" : ""}`}
                        >
                          {brideApplicant.nationalIdNumber || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Current Address
                        </div>
                        <div className="user-detail-field-value">
                          {brideApplicant.currentAddress || "—"}
                        </div>
                      </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Officiator's Details */}
                {officiator && (
                  <>
                    <h3 className="user-detail-section-title">
                      Officiator's Details
                    </h3>
                    <div className="user-detail-grid">
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Officiator Name
                        </div>
                        <div className="user-detail-field-value">
                          {officiator.officiatorName || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Officiator Contact
                        </div>
                        <div className="user-detail-field-value">
                          {officiator.officiatorContact || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Witness Name
                        </div>
                        <div className="user-detail-field-value">
                          {officiator.witnessName || "—"}
                        </div>
                      </div>
                      <div className="user-detail-field">
                        <div className="user-detail-field-label">
                          Witness Contact
                        </div>
                        <div className="user-detail-field-value">
                          {officiator.witnessContact || "—"}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Column 2: Action Buttons */}
              <div className="user-detail-actions">
                <button
                  className="user-detail-back user-detail-back--desktop"
                  onClick={() => navigate("/marriages/approval")}
                >
                  ← Back to Approvals
                </button>

                {application?.workflowId &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--success"
                        onClick={handleApprove}
                        title="Approve application"
                      >
                        <span className="action-icon">✅</span>
                        Approve
                      </button>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--warning"
                        onClick={handleQuery}
                        title="Query application"
                      >
                        <span className="action-icon">❓</span>
                        Query
                      </button>
                      <button
                        className="user-detail-action-btn user-detail-action-btn--danger"
                        onClick={handleReject}
                        title="Reject application"
                      >
                        <span className="action-icon">❌</span>
                        Reject
                      </button>
                    </>
                  )}

                <button
                  className="user-detail-action-btn"
                  onClick={() => setShowHistory(true)}
                >
                  <span className="action-icon">🕘</span>
                  History
                </button>
                {hasQueryRemarks && (
                  <button
                    className="user-detail-action-btn user-detail-action-btn--warning"
                    onClick={() => setShowQueryRemarks(true)}
                  >
                    <span className="action-icon">💬</span>
                    Query Remarks
                  </button>
                )}

                {/* Workflow Progress */}
                {application?.workflowId &&
                  (application?.statusId === UserStatusId.Submitted ||
                    application?.statusId === UserStatusId.Queried ||
                    application?.statusId === UserStatusId.InProgress) && (
                    <div style={{ marginTop: "2rem" }}>
                      <WorkflowStages
                        workflowId={application.workflowId}
                        currentStageOrder={currentStageOrder}
                      />
                    </div>
                  )}
              </div>
            </div>
          )}

          <History
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showHistory}
            onClose={() => setShowHistory(false)}
          />

          <QueryRemarks
            entityName="CertificateApplications"
            recordId={application?.id}
            isOpen={showQueryRemarks}
            onClose={() => setShowQueryRemarks(false)}
          />

          <QueryApplicationForm
            isOpen={showQueryForm}
            onClose={() => setShowQueryForm(false)}
            entityName="CertificateApplications"
            recordId={application?.id}
            workflowId={application?.workflowId}
            statusId={application?.statusId}
            onSubmit={handleSubmitQuery}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ApprovalDetail;
